import React, { useEffect, useMemo, useState } from "react";
import CommonSelect from "../../../core/common/commonSelect";
import Table from "../../../core/common/dataTable/index";
import { DatePicker, Input } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getStudentAttendance } from "../../../service/student-attendance";
import { getEmployeeAttendance } from "../../../service/staff-attendance";
import { getAllClasses, type Class } from "../../../service/classes";
import { listGroups, type Group } from "../../../service/group";
import { Option } from "../../../core/common/multiSelect";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { absoluteCampusLogoUrl, campusBrandName } from "../../../utils/campusLogos";
import { code39SvgString } from "../../../core/common/barcode/code39";

// Every matching roster loads at once — this screen prints a full sheet, it
// never paginates.
const ALL_ROSTER_LIMIT = 2000;

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const printCSS = `
  body { font-family: Arial, sans-serif; font-size: 11px; margin: 0; padding: 0; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #ccc; padding: 1px 2px; text-align: center; vertical-align: middle; }
  th { background: #2980b9; color: #fff; white-space: nowrap; font-size: 11px; }
  td { font-size: 11px; }
  @page { size: landscape; margin: 3mm; }
  .print-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; border-bottom: 1.5px solid #2980b9; padding-bottom: 3px; }
  .school-brand { display: flex; align-items: center; gap: 5px; }
  .school-brand img { height: 34px; object-fit: contain; }
  .school-brand strong { font-size: 13px; letter-spacing: 1px; }
  .report-meta { text-align: center; font-size: 12px; line-height: 1.5; }
  .sheet-info { text-align: right; font-size: 12px; line-height: 1.5; }
  td.name-cell { white-space: nowrap; text-align: left; }
  td.barcode-cell { text-align: center; }
  td.barcode-cell svg { height: 30px; width: auto; max-width: 170px; }
`;

const openAndPrint = (title: string, html: string) => {
  const win = window.open("", "_blank");
  if (!win) {
    toast.error("Popup blocked — allow popups and try again");
    return;
  }
  win.document.write(`<html><head><title>${title}</title><style>${printCSS}</style></head><body>${html}</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
    win.close();
  }, 300);
};

const buildDayHeaders = (year: number, month: number) => {
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const daysInMonth = new Date(year, month, 0).getDate();
  const headers: string[] = [];
  const isSunday: boolean[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month - 1, day);
    headers.push(`${day.toString().padStart(2, "0")}<br>${dayNames[d.getDay()]}`);
    isSunday[day] = d.getDay() === 0;
  }
  return { daysInMonth, headers, isSunday };
};

const StudentsBlankSheet = () => {
  const currentSession = useSelector(selectCurrentSession);
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentBranch = useSelector(selectCurrentBranch);

  const [classes, setClasses] = useState<Class[]>([]);
  const [classOptions, setClassOptions] = useState<Option[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const [filters, setFilters] = useState({
    month: dayjs().format("YYYY-MM"),
    system: undefined as string | undefined,
    classId: "",
    sectionId: "",
    groupId: "",
  });

  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [printing, setPrinting] = useState(false);

  const sectionOptions: Option[] = useMemo(() => {
    const selectedClass = classes.find((c) => c._id === filters.classId);
    return (selectedClass?.sections || []).map((section: any) => ({
      label: section.name,
      value: section._id,
    }));
  }, [classes, filters.classId]);

  useEffect(() => {
    if (!filters.system && systems.length) {
      setFilters((prev) => ({ ...prev, system: systems[0]._id }));
    }
  }, [systems, filters.system]);

  useEffect(() => {
    if (!filters.system) return;
    (async () => {
      try {
        setLoadingClasses(true);
        const res = await getAllClasses({
          limit: 100,
          system: filters.system,
          session: currentSession?._id,
        });
        setClasses(res.classes);
        setClassOptions(res.classes.map((c: Class) => ({ label: c.name, value: c._id })));
      } catch (error) {
        console.error("Error fetching classes:", error);
        toast.error("Failed to fetch classes");
      } finally {
        setLoadingClasses(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.system, currentSession?._id]);

  const fetchGroups = async (classId: string) => {
    if (!classId) {
      setGroups([]);
      return;
    }
    try {
      setLoadingGroups(true);
      const res = await listGroups({ system: filters.system, class: classId });
      setGroups(res?.data || []);
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleMonthChange = (date: dayjs.Dayjs | null) => {
    if (date) setFilters((prev) => ({ ...prev, month: date.format("YYYY-MM") }));
  };

  const handleSystemChange = (item: any) => {
    setFilters((prev) => ({ ...prev, system: item?._id, classId: "", sectionId: "", groupId: "" }));
    setGroups([]);
  };

  const handleClassChange = (opt: any) => {
    const classId = opt?.value || "";
    setFilters((prev) => ({ ...prev, classId, sectionId: "", groupId: "" }));
    fetchGroups(classId);
  };

  const handleSectionChange = (opt: any) =>
    setFilters((prev) => ({ ...prev, sectionId: opt?.value || "" }));

  const handleGroupChange = (opt: any) =>
    setFilters((prev) => ({ ...prev, groupId: opt?.value || "" }));

  // Month, System and Class are required (in that order); Section and Group
  // are optional — leaving them blank prints the whole class.
  const canSearch = Boolean(filters.month && filters.system && filters.classId);

  const handleSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await getStudentAttendance({
        classId: filters.classId,
        sectionId: filters.sectionId || undefined,
        groupId: filters.groupId || undefined,
        systemId: filters.system,
        sessionId: currentSession?._id,
        date: `${filters.month}-01`,
        page: 1,
        limit: ALL_ROSTER_LIMIT,
        sortBy: "grNumber",
        sortOrder: "asc",
      });
      if (res.success) {
        setRoster(res.data || []);
        if (!res.data?.length) toast.warning("No students found for this selection");
      } else {
        setRoster([]);
        toast.error("Failed to load roster");
      }
    } catch (error) {
      console.error("Error loading roster:", error);
      toast.error("Failed to load roster");
      setRoster([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters((prev) => ({ ...prev, classId: "", sectionId: "", groupId: "" }));
    setGroups([]);
    setRoster([]);
    setHasSearched(false);
  };

  const handlePrintBlank = () => {
    if (!roster.length) {
      toast.warning("No students to print");
      return;
    }
    setPrinting(true);
    try {
      const [yearStr, monthStr] = filters.month.split("-");
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      const { daysInMonth, headers: dayHeaders, isSunday } = buildDayHeaders(year, month);
      const headerRow = ["S#", "GR", "Student Name", "Barcode", ...dayHeaders];

      const bodyRows = roster
        .map((item, idx) => {
          const fullName = `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim();
          const gr = String(item.student?.grNumber || "").trim();
          const barcode = gr ? code39SvgString(gr, { height: 30, unit: 1, showText: false }) : "";
          const dayCells: string[] = [];
          for (let day = 1; day <= daysInMonth; day++) {
            dayCells.push(`<td>${isSunday[day] ? "Sun" : "&nbsp;"}</td>`);
          }
          return `<tr><td>${idx + 1}</td><td>${escapeHtml(gr)}</td><td class="name-cell">${escapeHtml(fullName)}</td><td class="barcode-cell">${barcode}</td>${dayCells.join("")}</tr>`;
        })
        .join("");

      const className = classOptions.find((o) => o.value === filters.classId)?.label || "";
      const sectionName = sectionOptions.find((o) => o.value === filters.sectionId)?.label || "All Sections";
      const monthLabel = dayjs(filters.month, "YYYY-MM").format("MMMM YYYY");
      const logoUrl = absoluteCampusLogoUrl(currentBranch?.blackLogoUrl);
      const brandName = escapeHtml(campusBrandName(currentBranch));

      const headerHtml = `
        <div class="print-header">
          <div class="school-brand">
            <img src="${logoUrl}" onerror="this.style.display='none'" />
            <strong>${brandName}</strong>
          </div>
          <div class="report-meta">
            <div>Class: ${escapeHtml(className)}</div>
            <div>Section: ${escapeHtml(sectionName)}</div>
          </div>
          <div class="sheet-info">
            <div>Blank Attendance Sheet</div>
            <div>${monthLabel}</div>
          </div>
        </div>
      `;

      const html = `${headerHtml}<table><thead><tr>${headerRow.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${bodyRows}</tbody></table>`;
      openAndPrint("Blank Attendance Sheet", html);
    } finally {
      setPrinting(false);
    }
  };

  const columns = [
    {
      title: "GR No",
      dataIndex: "student",
      render: (student: any) => student?.grNumber || "N/A",
    },
    {
      title: "Name",
      dataIndex: "user",
      render: (user: any) => `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "N/A",
    },
    {
      title: "Class",
      dataIndex: "class",
      render: (classData: any) => classData?.name || "N/A",
    },
    {
      title: "Section",
      dataIndex: "section",
      render: (section: any) => section?.name || "N/A",
    },
  ];

  return (
    <div className="card">
      <div className="card-header pb-3 d-flex align-items-start justify-content-between flex-wrap gap-2">
        <div>
          <h4 className="mb-1">Print Blank Student Attendance Sheet</h4>
          <p className="text-muted small mb-0">
            Pick a Month, System and Class (Section and Group are optional — leave them blank to
            include the whole class). Click <strong>Search</strong> to preview the roster.
          </p>
        </div>
        {hasSearched && roster.length > 0 && (
          <button
            type="button"
            className="btn btn-outline-light bg-white"
            onClick={handlePrintBlank}
            disabled={printing}
          >
            <i className="ti ti-printer me-1" />
            {printing ? "Preparing..." : "Print Blank Sheet"}
          </button>
        )}
      </div>

      <div className="px-3 pb-3 pt-3">
        <div className="row g-3 align-items-end">
          <div className="col-6 col-md-2">
            <label className="form-label fw-semibold">Month</label>
            <DatePicker
              picker="month"
              format="MMM-YY"
              value={dayjs(filters.month, "YYYY-MM")}
              onChange={handleMonthChange}
              allowClear={false}
              style={{ height: "38px", width: "100%" }}
            />
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label fw-semibold">System</label>
            <CommonSelect
              className="select"
              options={systems}
              value={systems.find((item: any) => item._id === filters.system)}
              getOptionValue={(item) => item._id}
              getOptionLabel={(item) => item.name}
              onChange={handleSystemChange}
              placeholder="Select System"
            />
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label fw-semibold">Class</label>
            <CommonSelect
              className="select"
              options={classOptions}
              value={classOptions.find((opt) => opt.value === filters.classId)}
              onChange={handleClassChange}
              placeholder={
                loadingClasses ? "Loading..." : !filters.system ? "Select System first" : "Select Class"
              }
              isDisabled={!filters.system || loadingClasses}
            />
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label fw-semibold">
              Section <span className="text-muted fw-normal">(optional)</span>
            </label>
            <CommonSelect
              className="select"
              options={sectionOptions}
              value={sectionOptions.find((opt) => opt.value === filters.sectionId)}
              onChange={handleSectionChange}
              placeholder={!filters.classId ? "Select Class first" : "All sections"}
              isDisabled={!filters.classId || sectionOptions.length === 0}
            />
          </div>

          {groups.length > 0 && (
            <div className="col-6 col-md-2">
              <label className="form-label fw-semibold">
                Group <span className="text-muted fw-normal">(optional)</span>
              </label>
              <CommonSelect
                className="select"
                options={groups.map((g) => ({ label: g.name, value: g._id }))}
                value={groups
                  .map((g) => ({ label: g.name, value: g._id }))
                  .find((opt) => opt.value === filters.groupId)}
                onChange={handleGroupChange}
                placeholder={loadingGroups ? "Loading..." : "All groups"}
                isDisabled={loadingGroups}
              />
            </div>
          )}

          <div className="col-6 col-md-2">
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary flex-grow-1"
                onClick={handleSearch}
                disabled={!canSearch}
                title={canSearch ? "Search" : "Select Month, System and Class first"}
              >
                <i className="ti ti-search me-1" />
                Search
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleReset}
                title="Clear Class, Section and Group"
              >
                <i className="ti ti-refresh" />
              </button>
            </div>
          </div>
        </div>

        {!canSearch && (
          <div className="small text-muted mt-2">
            <i className="ti ti-info-circle me-1" />
            Select Month, System and Class to search.
          </div>
        )}

        {hasSearched && (
          <div className="text-muted small mt-3">
            {loading ? "Loading…" : `${roster.length} student${roster.length === 1 ? "" : "s"} found`}
          </div>
        )}
      </div>

      {hasSearched && (
        <div className="card-body p-0 pb-3">
          <Table dataSource={roster} columns={columns} loading={loading} hidePagination />
        </div>
      )}
    </div>
  );
};

const StaffBlankSheet = () => {
  const currentSession = useSelector(selectCurrentSession);
  const currentBranch = useSelector(selectCurrentBranch);

  const [filters, setFilters] = useState({
    month: dayjs().format("YYYY-MM"),
    search: "",
  });

  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [printing, setPrinting] = useState(false);

  const handleMonthChange = (date: dayjs.Dayjs | null) => {
    if (date) setFilters((prev) => ({ ...prev, month: date.format("YYYY-MM") }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await getEmployeeAttendance({
        sessionId: currentSession?._id,
        date: `${filters.month}-01`,
        search: filters.search || undefined,
        page: 1,
        limit: ALL_ROSTER_LIMIT,
        sortBy: "name",
        sortOrder: "asc",
      });
      if (res.success) {
        setRoster(res.data || []);
        if (!res.data?.length) toast.warning("No staff found");
      } else {
        setRoster([]);
        toast.error("Failed to load staff roster");
      }
    } catch (error) {
      console.error("Error loading staff roster:", error);
      toast.error("Failed to load staff roster");
      setRoster([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters((prev) => ({ ...prev, search: "" }));
    setRoster([]);
    setHasSearched(false);
  };

  const handlePrintBlank = () => {
    if (!roster.length) {
      toast.warning("No staff to print");
      return;
    }
    setPrinting(true);
    try {
      const [yearStr, monthStr] = filters.month.split("-");
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      const { daysInMonth, headers: dayHeaders, isSunday } = buildDayHeaders(year, month);
      const headerRow = ["S#", "Emp ID", "Staff Name", "Department", "Barcode", ...dayHeaders];

      const bodyRows = roster
        .map((item, idx) => {
          const fullName = `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim();
          const code = String(item.employeeCode || "").trim();
          const barcode = code ? code39SvgString(code, { height: 30, unit: 1, showText: false }) : "";
          const dayCells: string[] = [];
          for (let day = 1; day <= daysInMonth; day++) {
            dayCells.push(`<td>${isSunday[day] ? "Sun" : "&nbsp;"}</td>`);
          }
          return `<tr><td>${idx + 1}</td><td>${escapeHtml(code)}</td><td class="name-cell">${escapeHtml(fullName)}</td><td>${escapeHtml(item.department || "")}</td><td class="barcode-cell">${barcode}</td>${dayCells.join("")}</tr>`;
        })
        .join("");

      const monthLabel = dayjs(filters.month, "YYYY-MM").format("MMMM YYYY");
      const logoUrl = absoluteCampusLogoUrl(currentBranch?.blackLogoUrl);
      const brandName = escapeHtml(campusBrandName(currentBranch));

      const headerHtml = `
        <div class="print-header">
          <div class="school-brand">
            <img src="${logoUrl}" onerror="this.style.display='none'" />
            <strong>${brandName}</strong>
          </div>
          <div class="report-meta"><div>All Staff</div></div>
          <div class="sheet-info">
            <div>Blank Attendance Sheet</div>
            <div>${monthLabel}</div>
          </div>
        </div>
      `;

      const html = `${headerHtml}<table><thead><tr>${headerRow.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${bodyRows}</tbody></table>`;
      openAndPrint("Blank Attendance Sheet", html);
    } finally {
      setPrinting(false);
    }
  };

  const columns = [
    {
      title: "Emp ID",
      dataIndex: "employeeCode",
      render: (code: string) => code || "N/A",
    },
    {
      title: "Name",
      dataIndex: "user",
      render: (user: any) => `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "N/A",
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (department: string) => department || "N/A",
    },
    {
      title: "Designation",
      dataIndex: "designation",
      render: (designation: string) => designation || "N/A",
    },
  ];

  return (
    <div className="card">
      <div className="card-header pb-3 d-flex align-items-start justify-content-between flex-wrap gap-2">
        <div>
          <h4 className="mb-1">Print Blank Staff Attendance Sheet</h4>
          <p className="text-muted small mb-0">
            Pick a Month (Search is optional to narrow by name/department), then click{" "}
            <strong>Search</strong> to preview the roster.
          </p>
        </div>
        {hasSearched && roster.length > 0 && (
          <button
            type="button"
            className="btn btn-outline-light bg-white"
            onClick={handlePrintBlank}
            disabled={printing}
          >
            <i className="ti ti-printer me-1" />
            {printing ? "Preparing..." : "Print Blank Sheet"}
          </button>
        )}
      </div>

      <div className="px-3 pb-3 pt-3">
        <div className="row g-3 align-items-end">
          <div className="col-6 col-md-2">
            <label className="form-label fw-semibold">Month</label>
            <DatePicker
              picker="month"
              format="MMM-YY"
              value={dayjs(filters.month, "YYYY-MM")}
              onChange={handleMonthChange}
              allowClear={false}
              style={{ height: "38px", width: "100%" }}
            />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label fw-semibold">
              Search <span className="text-muted fw-normal">(optional)</span>
            </label>
            <Input
              placeholder="Name or department"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              style={{ height: "38px" }}
            />
          </div>

          <div className="col-6 col-md-2">
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary flex-grow-1" onClick={handleSearch}>
                <i className="ti ti-search me-1" />
                Search
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleReset}
                title="Clear search"
              >
                <i className="ti ti-refresh" />
              </button>
            </div>
          </div>
        </div>

        {hasSearched && (
          <div className="text-muted small mt-3">
            {loading ? "Loading…" : `${roster.length} staff member${roster.length === 1 ? "" : "s"} found`}
          </div>
        )}
      </div>

      {hasSearched && (
        <div className="card-body p-0 pb-3">
          <Table dataSource={roster} columns={columns} loading={loading} hidePagination />
        </div>
      )}
    </div>
  );
};

const BlankAttendanceSheet = () => {
  const [tab, setTab] = useState<"students" | "staff">("students");

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Blank Attendance Sheet</h3>
          <p className="text-muted mb-0">
            Print a blank attendance sheet for manual, on-paper marking.
          </p>
        </div>

        <ul className="nav nav-tabs nav-tabs-solid mb-3" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              type="button"
              className={`nav-link${tab === "students" ? " active" : ""}`}
              onClick={() => setTab("students")}
            >
              <i className="ti ti-users me-2" />
              Students
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              className={`nav-link${tab === "staff" ? " active" : ""}`}
              onClick={() => setTab("staff")}
            >
              <i className="ti ti-users-group me-2" />
              Staff
            </button>
          </li>
        </ul>

        {tab === "students" ? <StudentsBlankSheet /> : <StaffBlankSheet />}
      </div>
    </div>
  );
};

export default BlankAttendanceSheet;
